import { useSyncExternalStore } from 'react';
import { isIOS } from '../utils/platform';

/**
 * useKeyboardInset — how many CSS px at the bottom of the web view the iOS
 * keyboard covers (0 while it is hidden).
 *
 * capacitor.config.json runs the Keyboard plugin with the canonical
 * `resize: "body"`: on iOS the plugin unhooks WKWebView's own keyboard handling
 * and only shrinks <body>. A `position: fixed` layer (every BottomSheet) is laid
 * out against the web view, not the body, so it kept its full height and the
 * keyboard slid over its bottom — the Atlas composer and the end of the
 * transcript vanished behind the keys. Fixed layers read this value and lift
 * themselves above the keyboard.
 *
 * Native iOS only: Android's adjustResize shrinks the whole web view and
 * browsers pan their visual viewport, so fixed layers are already clear there
 * and the value stays 0.
 *
 * One pair of plugin listeners serves every subscriber; it is attached with the
 * first and kept for the app's lifetime, so the value is never stale when a
 * sheet opens. The plugin import is dynamic (never loaded on web) and only its
 * methods are called — the plugin proxy itself is never awaited
 * (shared-docs/capacitor-native-setup.md → thenable-proxy trap).
 */

let inset = 0;
let listening = false;
const subscribers = new Set();

function setInset(next) {
  const value = Number.isFinite(next) && next > 0 ? Math.round(next) : 0;
  if (value === inset) return;
  inset = value;
  subscribers.forEach((notify) => notify());
}

function listen() {
  if (listening || !isIOS()) return;
  listening = true;
  (async () => {
    try {
      const { Keyboard } = await import('@capacitor/keyboard');
      await Keyboard.addListener('keyboardWillShow', (info) => setInset(info?.keyboardHeight));
      await Keyboard.addListener('keyboardWillHide', () => setInset(0));
    } catch (err) {
      console.warn('Keyboard listeners unavailable (non-fatal):', err);
    }
  })();
}

export function subscribeKeyboardInset(notify) {
  listen();
  subscribers.add(notify);
  return () => {
    subscribers.delete(notify);
  };
}

export function getKeyboardInset() {
  return inset;
}

const noKeyboard = () => 0;

export function useKeyboardInset() {
  return useSyncExternalStore(subscribeKeyboardInset, getKeyboardInset, noKeyboard);
}
